import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpenText, ChevronDown, ChevronUp, Clock3, ExternalLink, ShieldCheck, Sparkles, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cleanMarkdownBold } from '@/lib/utils';
import type { AIModel, Message } from '@/types';
import { getCollabRoleTitle } from './collabTypes';
import TypingIndicator from './TypingIndicator';

interface DeepResearchMessageCardProps {
  group: {
    id: string;
    userMessage?: Message;
    assistantMessages: Message[];
  };
  models: AIModel[];
}

const statusBadgeStyles: Record<string, string> = {
  researching: 'bg-sky-600 text-white hover:bg-sky-600',
  verifying: 'bg-amber-600 text-white hover:bg-amber-600',
  writing: 'bg-violet-600 text-white hover:bg-violet-600',
  complete: 'bg-emerald-600 text-white hover:bg-emerald-600',
  failed: 'bg-destructive text-destructive-foreground hover:bg-destructive',
};

const getModelName = (models: AIModel[], modelId?: string | null) => {
  if (!modelId) return 'Unknown model';
  return models.find((model) => model.id === modelId)?.name || 'Unavailable model';
};

const renderContent = (content?: string) => {
  if (!content) return null;
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanMarkdownBold(content)}</ReactMarkdown>
    </div>
  );
};

export default function DeepResearchMessageCard({ group, models }: DeepResearchMessageCardProps) {
  const [detailsExpanded, setDetailsExpanded] = useState(true);

  const assistantMessages = useMemo(
    () => [...group.assistantMessages].sort((a, b) => {
      const orderDelta = (a.collab_order ?? 0) - (b.collab_order ?? 0);
      if (orderDelta !== 0) return orderDelta;
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    }),
    [group.assistantMessages]
  );

  const reportMessage = assistantMessages.find((message) => message.metadata?.research?.isFinalReport) || assistantMessages[assistantMessages.length - 1];
  const summaryMessage = assistantMessages.find((message) => message.collab_role === 'orchestrator');
  const overallStatus =
    reportMessage?.metadata?.research?.overallStatus ||
    summaryMessage?.metadata?.research?.overallStatus ||
    (assistantMessages.some((message) => message.collab_status === 'error')
      ? 'failed'
      : assistantMessages.some((message) => message.collab_status === 'pending' || message.collab_status === 'running')
        ? 'researching'
        : 'complete');

  const sourceList = reportMessage?.metadata?.research?.sources || [];
  const limitations = reportMessage?.metadata?.research?.limitations || [];
  const confidence = reportMessage?.metadata?.research?.confidence;
  const citationCheck = reportMessage?.metadata?.research?.citationCheck;

  return (
    <div className="rounded-2xl border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="bg-fuchsia-600 text-white hover:bg-fuchsia-600">Deep Research</Badge>
          <Badge className={statusBadgeStyles[overallStatus] || statusBadgeStyles.researching}>
            {overallStatus === 'complete'
              ? 'Complete'
              : overallStatus === 'failed'
                ? 'Failed'
                : overallStatus === 'writing'
                  ? 'Writing'
                  : overallStatus === 'verifying'
                    ? 'Verifying'
                    : 'Researching'}
          </Badge>
          <span className="text-sm text-muted-foreground">{assistantMessages.length} workflow stage{assistantMessages.length === 1 ? '' : 's'}</span>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => setDetailsExpanded((value) => !value)}>
          {detailsExpanded ? (
            <>
              Hide progress
              <ChevronUp className="ml-1 h-4 w-4" />
            </>
          ) : (
            <>
              Show progress
              <ChevronDown className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      <div className="mt-4 rounded-xl border bg-muted/20 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4" />
          Research request
        </div>
        <div className="text-sm whitespace-pre-wrap text-foreground">{group.userMessage?.content || 'Prompt unavailable'}</div>
      </div>

      {detailsExpanded ? (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Clock3 className="h-4 w-4 text-fuchsia-500" />
            Live workflow progress
          </div>
          <div className="space-y-3">
            {assistantMessages.map((message) => {
              const research = message.metadata?.research;
              const pending = message.collab_status === 'pending' || message.collab_status === 'running';
              const failed = message.collab_status === 'error';
              const stageLabel = research?.researchPhase?.replace(/_/g, ' ');

              return (
                <div
                  key={message.id}
                  className={`rounded-xl border p-4 ${failed ? 'border-destructive/40 bg-destructive/5' : 'bg-background'}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{getCollabRoleTitle((message.collab_role || 'report_writer') as any)}</span>
                      {stageLabel ? <Badge variant="outline">{stageLabel}</Badge> : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{getModelName(models, message.collab_model || message.model)}</Badge>
                      {pending ? <Badge variant="outline">In progress</Badge> : failed ? <Badge variant="destructive">Failed</Badge> : <Badge variant="secondary">Done</Badge>}
                    </div>
                  </div>

                  {pending ? (
                    <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{message.content || 'Running this stage…'}</span>
                      <TypingIndicator />
                    </div>
                  ) : failed ? (
                    <p className="mt-3 text-sm text-destructive">{message.content}</p>
                  ) : (
                    <div className="mt-3 space-y-3">
                      {renderContent(message.content)}
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {typeof research?.sourceCount === 'number' ? <Badge variant="outline">{research.sourceCount} sources</Badge> : null}
                        {research?.verificationStatus ? <Badge variant="outline">Verification: {research.verificationStatus}</Badge> : null}
                        {research?.citationStatus ? <Badge variant="outline">Citations: {research.citationStatus}</Badge> : null}
                        {typeof research?.confidenceScore === 'number' ? <Badge variant="outline">Confidence: {research.confidenceScore}/100</Badge> : null}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <Separator className="my-4" />

      <div className="rounded-2xl border border-fuchsia-300 bg-fuchsia-50/70 p-4 dark:border-fuchsia-700 dark:bg-fuchsia-950/20">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-fuchsia-700 dark:text-fuchsia-300">
            <BookOpenText className="h-4 w-4" />
            Final research report
            <Sparkles className="h-4 w-4" />
          </div>
          <Badge className="bg-violet-600 text-white hover:bg-violet-600">{getModelName(models, reportMessage?.collab_model || reportMessage?.model)}</Badge>
        </div>

        {reportMessage?.collab_status === 'pending' || reportMessage?.collab_status === 'running' || !reportMessage?.content ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Waiting for the cited report draft</span>
            <TypingIndicator />
          </div>
        ) : reportMessage?.collab_status === 'error' ? (
          <p className="text-sm text-destructive">{reportMessage.content}</p>
        ) : (
          <div className="space-y-4">
            {renderContent(reportMessage.content)}

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-xl border bg-background/70 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  Confidence and validation
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {confidence ? <p>Confidence: <span className="font-medium text-foreground">{confidence.label.toUpperCase()} ({confidence.score}/100)</span></p> : null}
                  {citationCheck ? <p>Citation coverage: <span className="font-medium text-foreground">{citationCheck.coverageScore}%</span></p> : null}
                  {confidence?.factors?.length ? (
                    <ul className="ml-4 list-disc space-y-1">
                      {confidence.factors.map((factor) => (
                        <li key={factor}>{factor}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border bg-background/70 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <ExternalLink className="h-4 w-4 text-sky-600" />
                  Source set
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>{sourceList.length} validated source{sourceList.length === 1 ? '' : 's'} retained.</p>
                  {sourceList.slice(0, 4).map((source) => (
                    <div key={source.id} className="rounded-lg border px-2 py-1">
                      <div className="font-medium text-foreground">[{source.id}] {source.title}</div>
                      {source.url ? <div className="truncate text-xs">{source.url}</div> : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {limitations.length > 0 ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/20">
                <div className="mb-2 font-semibold text-amber-900 dark:text-amber-200">Limitations</div>
                <ul className="ml-4 list-disc space-y-1 text-amber-800 dark:text-amber-100">
                  {limitations.map((limitation) => (
                    <li key={limitation}>{limitation}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
